"use client";

import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Methodology() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: diagramRef,
    isVisible: diagramVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: specsRef,
    isVisible: specsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Input Analysis",
    description: "Ingests the full step-by-step working a student submits, alongside the official mark scheme for that question."
  }, {
    number: "02",
    title: "Process Mapping",
    description: "Matches each step first against a library of known correct and error patterns tied to specific mark-scheme criteria."
  }, {
    number: "03",
    title: "Failure Detection",
    description: "Classifies each step as correct, a procedural slip (right method, wrong execution), or a knowledge gap (the method itself is wrong)."
  }, {
    number: "04",
    title: "Logic Verification",
    description: "Any step the pattern library can't resolve confidently is escalated for a deeper contextual review before a final classification is set."
  }, {
    number: "05",
    title: "Feedback Synthesis",
    description: "Every classification carries a stated justification tied to the exact mark-scheme criterion it resolves against, so the reasoning is traceable, not a black box."
  }, {
    number: "06",
    title: "Progress Tracking",
    description: "Classified submissions accumulate into a growing dataset that continually refines and expands the pattern library over time."
  }];
  const callouts = [{
    x: 18,
    y: 22
  }, {
    x: 82,
    y: 18
  }, {
    x: 14,
    y: 50
  }, {
    x: 86,
    y: 50
  }, {
    x: 22,
    y: 78
  }, {
    x: 78,
    y: 82
  }];
  return <section data-section-id="3497" id="our-methodology" className="relative overflow-hidden py-32 md:py-48 lg:py-[200px] bg-background text-foreground">
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
        <div ref={headerRef} className={`mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-[0.2em] mb-6 md:mb-8 text-primary">
            Infrastructure · 02
          </p>
          <h2 className="font-default font-light leading-[0.95] tracking-tight max-w-[14ch]" style={{
          fontSize: "clamp(56px, 8vw, 120px)"
        }}>
            Diagnostic
            <br />
            <span className="text-primary">Engine.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-24 md:mb-32">
          <div ref={diagramRef} className={`lg:col-span-7 lg:col-start-1 transition-all duration-1000 ease-out ${diagramVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="relative aspect-square w-full max-w-[640px] mx-auto">
              <svg viewBox="0 0 600 600" className="absolute inset-0 w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="300" cy="300" r="260" stroke="hsl(39 100% 80%)" strokeWidth="0.6" opacity="0.5" />
                <circle cx="300" cy="300" r="240" stroke="currentColor" strokeWidth="0.4" opacity="0.3" />
                <circle cx="300" cy="300" r="210" stroke="hsl(39 100% 80%)" strokeWidth="0.5" opacity="0.4" />
                {Array.from({
                length: 36
              }).map((_, i) => {
                const angle = i * 10 * Math.PI / 180;
                const x1 = 300 + Math.cos(angle) * 180;
                const y1 = 300 + Math.sin(angle) * 180;
                const x2 = 300 + Math.cos(angle) * 210;
                const y2 = 300 + Math.sin(angle) * 210;
                return <line key={i} data-index={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="hsl(39 100% 80%)" strokeWidth="0.3" opacity="0.4" />;
              })}
                <circle cx="300" cy="300" r="170" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
                <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="0.3" opacity="0.4" />
                {Array.from({
                length: 8
              }).map((_, i) => <circle key={`trace-${i}`} data-index={i} cx="300" cy="300" r={155 - i * 18} stroke="hsl(39 100% 80%)" strokeWidth="0.4" opacity={0.7 - i * 0.05} strokeDasharray="2 3" />)}
                {Array.from({
                length: 12
              }).map((_, i) => {
                const angle = i * 30 * Math.PI / 180;
                const x = 300 + Math.cos(angle) * 135;
                const y = 300 + Math.sin(angle) * 135;
                return <g key={`mag-${i}`} data-index={i}>
                      <rect x={x - 6} y={y - 2} width="12" height="4" fill="hsl(39 100% 80%)" opacity="0.6" transform={`rotate(${i * 30} ${x} ${y})`} />
                    </g>;
              })}
                <circle cx="300" cy="300" r="40" stroke="currentColor" strokeWidth="0.6" opacity="0.7" />
                <circle cx="300" cy="300" r="28" stroke="hsl(39 100% 80%)" strokeWidth="0.5" opacity="0.8" />
                <circle cx="300" cy="300" r="3" fill="hsl(39 100% 80%)" opacity="0.9" />

                {callouts.map((c, i) => {
                const cx = c.x / 100 * 600;
                const cy = c.y / 100 * 600;
                const dx = cx - 300;
                const dy = cy - 300;
                const len = Math.sqrt(dx * dx + dy * dy);
                const innerX = 300 + dx / len * 130;
                const innerY = 300 + dy / len * 130;
                const isActive = activeIndex === i;
                return <line key={`anno-${i}`} data-index={i} x1={innerX} y1={innerY} x2={cx} y2={cy} stroke={isActive ? "hsl(39 100% 80%)" : "hsl(39 100% 80%)"} strokeWidth={isActive ? "1" : "0.5"} opacity={isActive ? "1" : "0.6"} strokeDasharray="3 2" className="transition-all duration-300" />;
              })}
              </svg>

              {callouts.map((c, i) => {
              const isActive = activeIndex === i;
              return <div key={`callout-${i}`} data-index={i} className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-300" style={{
                left: `${c.x}%`,
                top: `${c.y}%`
              }}>
                    <div className={`flex items-center gap-2 transition-all duration-300 ${isActive ? "scale-110" : ""}`}>
                      <span className="flex items-center justify-center w-7 h-7 rounded-lg font-mono text-[10px] font-medium transition-all duration-300" style={{
                    backgroundColor: isActive ? "hsl(39 100% 80%)" : "hsl(0 0% 98%)",
                    color: isActive ? "hsl(254 70% 5%)" : "hsl(254 70% 5%)"
                  }}>
                        {features[i].number}
                      </span>
                      <span className="font-mono text-[11px] uppercase tracking-wider whitespace-nowrap transition-colors duration-300" style={{
                    color: isActive ? "hsl(39 100% 80%)" : "#C9C4D6"
                  }}>
                        {features[i].title}
                      </span>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={cardsRef} className={`lg:col-span-5 lg:col-start-8 flex flex-col gap-4 transition-all duration-1000 ease-out ${cardsVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {features.map((feature, i) => {
            const isActive = activeIndex === i;
            return <Card key={i} data-index={i} onMouseEnter={() => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} className="cursor-pointer border transition-all duration-300 py-5 rounded-lg shadow-md" style={{
              backgroundColor: isActive ? "hsl(254 70% 12%)" : "transparent",
              borderColor: isActive ? "hsl(39 100% 80%)" : "hsl(254 20% 25%)"
            }}>
                  <CardContent className="px-5 flex gap-5 items-start">
                    <span className="font-mono text-xs mt-1 transition-colors duration-300" style={{
                  color: isActive ? "hsl(39 100% 80%)" : "#C9C4D6"
                }}>
                      {feature.number}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-default font-medium mb-1.5 transition-colors duration-300" style={{
                    fontSize: "22px",
                    lineHeight: "1.2"
                  }}>
                        {feature.title}
                      </h3>
                      <p className="font-default font-light" style={{
                    fontSize: "15px",
                    lineHeight: "1.55",
                    color: "#C9C4D6"
                  }}>
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <div ref={specsRef} className={`border-t pt-16 md:pt-20 transition-all duration-1000 ease-out ${specsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        borderColor: "hsl(254 20% 25%)"
      }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-default font-light max-w-md" style={{
            fontSize: "16px",
            color: "#C9C4D6"
          }}>
              A technical overview of how Setura maps student performance to academic mark-schemes.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                <Link to="/TechnicalDocumentationPage">
                  View Technical Documentation
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg border bg-transparent hover:bg-primary hover:text-primary-foreground transition-colors" style={{
              borderColor: "hsl(39 100% 80%)",
              color: "hsl(39 100% 80%)"
            }}>
                <Link to="/PilotPage">
                  Book a pilot call
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}